import { useMutation } from "@apollo/client/react";
import MontuToastProvider from "@repo/components/src/globalProviders/toast";
import { Button, ButtonIcon } from "@repo/components/src/ui/button";
import { Icon } from "@repo/components/src/ui/icon";
import { Toast, ToastTitle } from "@repo/components/src/ui/toast";
import { CampaignOverviewDocument } from "@repo/graphql/documents/campaign/query.generated";
import {
  BulkShortListPostulationDocument,
  RejectPostulationsDocument,
} from "@repo/graphql/documents/postulation/mutation.generated";
import {
  ActivePostulationsDocument,
  CampaignSummaryPostulationsDocument,
  PostulationsDocument,
} from "@repo/graphql/documents/postulation/query.generated";
import { CheckIcon, X } from "lucide-react-native";

export const showApplicationActionToast = (
  title: string,
  action: "success" | "error",
) => {
  MontuToastProvider.show({
    placement: "top",
    duration: 3000,
    render: ({ id }) => (
      <Toast
        nativeID={`toast-${id}`}
        action={action}
        variant="solid"
        className="bg-background-0 border border-outline-200 rounded-xl shadow-lg flex-row items-center gap-3 px-4 py-3 min-w-[320px]"
      >
        <Icon
          as={CheckIcon}
          size="sm"
          className={`stroke-2 ${action === "success" ? "text-success-600" : "text-error-600"}`}
        />
        <ToastTitle
          size="sm"
          className="flex-1 shrink text-typography-800 font-medium"
        >
          {title}
        </ToastTitle>
        <Button
          action="secondary"
          variant="link"
          size="xs"
          onPress={() => MontuToastProvider.close(id)}
        >
          <ButtonIcon as={X} size="sm" className="text-typography-400" />
        </Button>
      </Toast>
    ),
  });
};

type Options = {
  onRejectComplete?: () => void;
  onLikeComplete?: () => void;
};

const REFETCH = [
  CampaignSummaryPostulationsDocument,
  ActivePostulationsDocument,
  CampaignOverviewDocument,
  // Casting table/cards list — refetch so a preselected/rejected applicant
  // drops out of "En revisión" and moves to the preselección step.
  PostulationsDocument,
];

export const useApplicationActions = (
  postulationId: string | undefined,
  options: Options = {},
) => {
  const [rejectPostulations, { loading: loadingReject }] = useMutation(
    RejectPostulationsDocument,
    {
      refetchQueries: REFETCH,
      awaitRefetchQueries: true,
      onCompleted: () => {
        showApplicationActionToast("Postulación rechazada", "error");
        options.onRejectComplete?.();
      },
    },
  );

  const [bulkShortlist, { loading: loadingLike }] = useMutation(
    BulkShortListPostulationDocument,
    {
      refetchQueries: REFETCH,
      awaitRefetchQueries: true,
      onCompleted: (data) => {
        // A creator the backend refuses (a sanctioned one) comes back as a user
        // error, not a failed request: nothing was added, so nothing to celebrate.
        const refused = data.postulation.bulkShortlist?.userErrors?.[0];
        if (refused) {
          showApplicationActionToast(refused.message, "error");
          return;
        }
        showApplicationActionToast("Agregado a la preselección", "success");
        options.onLikeComplete?.();
      },
      onError: () =>
        showApplicationActionToast("No pudimos preseleccionar", "error"),
    },
  );

  const reject = () => {
    if (!postulationId) return;
    void rejectPostulations({ variables: { postulationIds: [postulationId] } });
  };
  const like = () => {
    if (!postulationId) return;
    void bulkShortlist({ variables: { postulationIds: [postulationId] } });
  };

  return {
    reject,
    like,
    loadingReject,
    loadingLike,
    loading: loadingReject || loadingLike,
  };
};
